import React, { useState, forwardRef } from "react";
import "./mobile-input.css";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

const MobileInput = React.memo(forwardRef(({ type = "text", placeholder, name, required = false, icon, value, onChange, variant = "default", disabled = false, isLoading = false, ...props }, ref) => {
    const localRef = React.useRef(null);
    const [showPassword, setShowPassword] = useState(false);

    // Sync forwarded ref with local ref
    React.useImperativeHandle(ref, () => localRef.current);
    const isPassword = type === "password";

    const togglePassword = () => {
        setShowPassword(!showPassword);
    };

    const inputType = isPassword ? (showPassword ? "text" : "password") : type;

    const renderLabel = () => {
        if (props.label) {
            return <label className="mobile-input-label">{props.label}</label>;
        }
        return null;
    };

    if (type === "phone") {
        return (
            <div className={`mobile-input-wrapper w-100 position-relative ${variant} ${disabled ? 'is-disabled' : ''}`}>
                {renderLabel()}
                <PhoneInput
                    defaultCountry="co"
                    // Limit countries to reduce SVG overhead (Flags)
                    countries={[
                        ['Colombia', 'co', '57'],
                        ['Mexico', 'mx', '52'],
                        ['United States', 'us', '1'],
                        ['Spain', 'es', '34'],
                        ['Peru', 'pe', '51'],
                        ['Chile', 'cl', '56'],
                        ['Argentina', 'ar', '54'],
                    ]}
                    name={name}
                    value={value || ""}
                    onChange={onChange}
                    required={required}
                    disabled={disabled}
                    className="w-100"
                    containerStyle={{
                        width: "100%"
                    }}
                    {...props}
                />
                {props.error && (
                    <small className="mobile-input-error">{props.error}</small>
                )}
            </div>
        );
    }

    if (type === "file") {
        return (
            <div className={`mobile-input-wrapper w-100 ${variant} ${disabled ? 'is-disabled' : ''}`}>
                {renderLabel()}
                <div className="mobile-file-input-wrapper">
                    <input
                        type="file"
                        name={name}
                        className="mobile-file-input"
                        onChange={onChange}
                        required={required}
                        disabled={disabled}
                        {...props}
                    />
                    <span className={`mobile-file-label ${value ? 'has-file' : ''}`}>
                        <span className="mobile-file-name">
                            {value ? value.name : placeholder}
                        </span>
                        <i className="bi bi-upload"></i>
                    </span>
                </div>
                {props.error && (
                    <small className="mobile-input-error">{props.error}</small>
                )}
            </div>
        );
    }

    return (
        <div
            className={`mobile-input-wrapper w-100 position-relative ${variant} ${disabled ? 'is-disabled' : ''}`}
            onClick={() => {
                if (disabled) return;
                if (type === 'date' && localRef.current) {
                    if (localRef.current.showPicker) {
                        localRef.current.showPicker();
                    } else {
                        localRef.current.focus();
                        localRef.current.click();
                    }
                }
            }}
        >
            {renderLabel()}
            <div className="position-relative">
                <input
                    ref={localRef}
                    type={inputType}
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    className="mobile-input shadow-none"
                    required={required}
                    disabled={disabled}
                    {...props}
                />
                {isPassword && (
                    <span
                        className="mobile-input-icon position-absolute"
                        onClick={togglePassword}
                        role="button"
                    >
                        <i className={showPassword ? "bi bi-eye-slash-fill" : "bi bi-eye-fill"}></i>
                    </span>
                )}
                {!isPassword && (icon || isLoading) && (
                    <span className="mobile-input-icon position-absolute">
                        {isLoading ? (
                            <i className="bi bi-arrow-repeat mobile-input-spinner"></i>
                        ) : (
                            icon
                        )}
                    </span>
                )}
            </div>
            {props.error && (
                <small className="mobile-input-error">{props.error}</small>
            )}
        </div>
    );
}));

MobileInput.displayName = 'MobileInput';

export default MobileInput;
